/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, DestroyRef, OnInit, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { AppConfigService, NotificationService, UserPreferencesService } from '@alfresco/adf-core';
import { PageHeaderComponent } from '@alfresco/aca-shared';
import { PeopleApi } from '@alfresco/js-api';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ThemeService } from '../theme/theme.service';
import { BrandingService } from '../branding/branding.service';
import { UserAvatarService } from '../branding/user-avatar.service';
import { DocumentExpiryComponent } from '../expiry/document-expiry.component';
import { MetadataFieldsComponent } from '../metadata-fields/metadata-fields.component';
import { UsersPageComponent } from '../users/users-page.component';
import { GroupsPageComponent } from '../groups/groups-page.component';
import { AuditLogComponent } from '../admin/audit-log.component';
import { PdpaReportComponent } from '../pdpa/pdpa-report.component';
import { WorkflowManageComponent } from '../workflow/workflow-manage.component';
import { EmailTemplatesComponent } from '../expiry/email-templates.component';
import { AssistantSettingsComponent } from '../assistant/assistant-settings.component';
import { WatermarkSettingsComponent } from '../watermark/watermark-settings.component';
import { MenuPermissionsComponent } from '../menu-permissions/menu-permissions.component';
import { JobsPageComponent } from '../jobs/jobs-page.component';
import { MigrationCenterComponent } from '../migration/migration-center.component';
import { SigningSettingsComponent } from '../pdf-studio/signing-settings.component';
import { MenuPermissionsService } from '../menu-permissions/menu-permissions.service';
import { WorkflowSettingsService } from '../workflow/workflow-settings.service';
import { AppLanguageService } from '../shared/language.service';
import { DisplayPreferencesService } from '../shared/display-preferences.service';
import { MailService } from '@alfresco/aca-content';
import { JobsService } from '../jobs/jobs.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

type SettingsSection = 'profile' | 'admin' | 'usage' | 'fields' | 'retention' | 'workflow' | 'migration' | 'assistant';

/** One of the administration screens, reached from the category of that name. */
/** Which administration screen a card opens, in place. */
type AdminToolId = 'users' | 'groups' | 'audit' | 'pdpa' | 'mail' | 'watermark' | 'menuPerms' | 'jobs' | 'signing';

interface AdminTool {
  id: AdminToolId;
  icon: string;
  title: string;
  description: string;
}

interface Person {
  id: string;
  displayName: string;
  email: string;
  jobTitle: string;
  enabled: boolean;
  isAdmin: boolean;
}

interface RepositoryInfo {
  edition: string;
  version: string;
  modules: number;
  readOnly: boolean;
  auditEnabled: boolean;
}

/** Languages offered by `app.config.json`, and the ADF preference behind them. */
interface LanguageOption {
  key: string;
  label: string;
}

/**
 * Settings (UI-06).
 *
 * A category list beside the panel it selects, as the reference lays it out.
 * Each panel shows what the repository or the application can actually supply;
 * where a category has nothing behind it in this deployment, it says so
 * instead of offering a control that would not do anything.
 */
@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatTooltipModule,
    PageHeaderComponent,
    DocumentExpiryComponent,
    MetadataFieldsComponent,
    UsersPageComponent,
    GroupsPageComponent,
    AuditLogComponent,
    PdpaReportComponent,
    WorkflowManageComponent,
    EmailTemplatesComponent,
    TranslatePipe,
    AssistantSettingsComponent,
    WatermarkSettingsComponent,
    MenuPermissionsComponent,
    JobsPageComponent,
    MigrationCenterComponent,
    SigningSettingsComponent
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss'
})
export class SettingsComponent implements OnInit {
  /** Alfresco scales the avatar itself; this only keeps a stray upload sane. */
  private static readonly MAX_AVATAR_BYTES = 2 * 1024 * 1024;

  /** Set when the administrator asked for the shipped logo back, so that Save
   *  writes an empty logo instead of leaving the stored one in place. */
  private brandLogoCleared = false;

  private readonly api = inject(AlfrescoApiService);
  private readonly config = inject(AppConfigService);
  private readonly notification = inject(NotificationService);
  private readonly displayPrefs = inject(DisplayPreferencesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly userPreferences = inject(UserPreferencesService);
  private readonly avatar = inject(UserAvatarService);
  private readonly languageService = inject(AppLanguageService);
  private readonly translate = inject(TranslateService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  readonly theme = inject(ThemeService);
  readonly branding = inject(BrandingService);
  readonly workflowSettings = inject(WorkflowSettingsService);
  readonly menuPerms = inject(MenuPermissionsService);

  /**
   * Languages the application ships translations for, from `app.config.json`.
   *
   * ADF keeps the chosen locale in browser storage, and a stored value wins over
   * the one in `app.config.json` for the life of that browser. Until this
   * control existed there was no way to change it from the application, so a
   * browser that first opened the app while the default was English stayed
   * English no matter what the configuration said.
   */
  languages: LanguageOption[] = [];

  language = '';

  /** The browser's own zone. ACS stores no per-user timezone to set. */
  readonly timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  readonly sections: { id: SettingsSection; title: string; description: string; icon: string }[] = [
    { id: 'profile', title: 'DMS.UI.E2357B', description: 'DMS.UI.88F00D', icon: 'account_circle' },
    { id: 'admin', title: 'DMS.UI.091FB6', description: 'DMS.UI.0FD84A', icon: 'admin_panel_settings' },
    { id: 'usage', title: 'DMS.UI.93BAE0', description: 'DMS.UI.0BB86D', icon: 'tune' },
    { id: 'fields', title: 'DMS.FIELDS.TITLE', description: 'DMS.FIELDS.SUBTITLE', icon: 'view_list' },
    { id: 'retention', title: 'DMS.UI.8F7F73', description: 'DMS.UI.186E9A', icon: 'schedule' },
    { id: 'workflow', title: 'DMS.WORKFLOW.TITLE', description: 'DMS.WORKFLOW.NAV_DESC', icon: 'design_services' },
    { id: 'migration', title: 'DMS.MIGRATION.TITLE', description: 'DMS.MIGRATION.NAV_DESC', icon: 'cloud_sync' },
    { id: 'assistant', title: 'DMS.ASSISTANT.TITLE', description: 'DMS.ASSISTANT.NAV_DESC', icon: 'smart_toy' }
  ];

  /**
   * The administration screens.
   *
   * They used to hang off the navigation as a submenu under "การตั้งค่า"; that
   * entry now opens this page instead, so this category is the way in and every
   * one of them has to be listed here or it becomes unreachable.
   */
  readonly adminTools: AdminTool[] = [
    { id: 'users', icon: 'manage_accounts', title: 'DMS.UI.F12543', description: 'DMS.UI.F17200' },
    { id: 'groups', icon: 'groups', title: 'DMS.UI.0B9CC7', description: 'DMS.UI.BDF400' },
    { id: 'audit', icon: 'fact_check', title: 'DMS.UI.84B424', description: 'DMS.UI.1FF39A' },
    { id: 'pdpa', icon: 'policy', title: 'DMS.PDPA.TITLE', description: 'DMS.PDPA.TOOL_DESC' },
    { id: 'mail', icon: 'mail', title: 'DMS.UI.5C6281', description: 'DMS.UI.CEEEB8' },
    { id: 'watermark', icon: 'branding_watermark', title: 'DMS.WATERMARK.TITLE', description: 'DMS.WATERMARK.TOOL_DESC' },
    { id: 'menuPerms', icon: 'visibility', title: 'DMS.MENUPERM.TITLE', description: 'DMS.MENUPERM.TOOL_DESC' },
    { id: 'jobs', icon: 'schedule_send', title: 'DMS.JOBS.TITLE', description: 'DMS.JOBS.TOOL_DESC' },
    { id: 'signing', icon: 'verified_user', title: 'DMS.SIGNING.TITLE', description: 'DMS.SIGNING.TOOL_DESC' }
  ];

  /**
   * The administration screen open inside the panel, if any.
   *
   * They used to be routes of their own, which meant leaving the settings page
   * to reach one and going back to reach the next. They open here instead, so
   * the category rail stays where it is.
   */
  readonly openTool = signal<AdminTool | null>(null);

  private readonly mail = inject(MailService);
  private readonly jobs = inject(JobsService);

  /** How sharing and test mail leave — the channel chosen on the mail screen. */
  readonly mailChannel = computed(() => `DMS.MAIL.MODE_${this.mail.settings().mode.toUpperCase()}`);

  /** Whether the jobs sidecar can send scheduled notices; null until it answers. */
  readonly smtpState = signal<'on' | 'off' | 'unreachable' | null>(null);

  showTool(tool: AdminTool): void {
    this.openTool.set(tool);
    if (tool.id === 'mail') {
      this.mail.load();
      this.jobs.health().subscribe((health) => this.smtpState.set(health ? (health.mail ? 'on' : 'off') : 'unreachable'));
    }
  }

  closeTool(): void {
    this.openTool.set(null);
  }

  readonly person = signal<Person | null>(null);
  readonly repository = signal<RepositoryInfo | null>(null);

  active: SettingsSection = 'profile';

  /** The avatar as the repository serves it; empty when the person has none. */
  readonly avatarUrl = this.avatar.url;

  readonly avatarBusy = signal(false);

  /** Branding is edited in place and written only when Save is pressed. */
  brandName = '';
  /** A newly picked logo, as a data URI, before it is saved. */
  brandLogo = '';
  /** The brand colour being edited, always a `#rrggbb` the picker accepts. */
  brandPrimary = BrandingService.DEFAULT_PRIMARY;

  readonly colourPresets = BrandingService.PRESETS;
  readonly defaultPrimary = BrandingService.DEFAULT_PRIMARY;
  readonly brandBusy = signal(false);
  readonly workflowBusy = signal(false);
  pageSize = DisplayPreferencesService.PAGE_SIZES[0];
  landing = DisplayPreferencesService.DEFAULT_LANDING;

  readonly pageSizes = DisplayPreferencesService.PAGE_SIZES;
  readonly landings = DisplayPreferencesService.LANDINGS;

  ngOnInit(): void {
    // the migration center has a real route (settings/data-migration) so its
    // sections can be deep-linked; the route data says which rail entry to open
    const routedSection = this.route.snapshot.data?.['section'] as SettingsSection | undefined;
    if (routedSection && this.sections.some((s) => s.id === routedSection)) {
      this.active = routedSection;
    }
    this.languages = this.config.get<LanguageOption[]>('languages', []) ?? [];
    this.language = this.userPreferences.locale ?? '';
    this.loadPerson();
    this.loadRepository();
    this.loadPreferences();
    this.branding.load();
    this.menuPerms.load();
    this.menuPerms.whenLoaded.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.ensureActiveAllowed());
    this.avatar.refresh();
    this.brandName = this.branding.name();
    this.brandPrimary = this.branding.primary() || BrandingService.DEFAULT_PRIMARY;
  }

  // ---- profile picture ------------------------------------------------------

  /**
   * Sends the picked image to the repository as this person's avatar.
   *
   * Alfresco derives the "avatar" rendition from it, so the URL is only asked
   * for again once the upload has come back — and with the person's new avatar
   * id, which is what makes the browser fetch the new image rather than the one
   * it already has.
   */
  uploadAvatar(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      this.notification.showError(this.translate.instant('DMS.UI.A39AA9'));
      return;
    }
    if (file.size > SettingsComponent.MAX_AVATAR_BYTES) {
      this.notification.showError(this.translate.instant('DMS.UI.7BC8A0'));
      return;
    }

    this.avatarBusy.set(true);
    const api = new PeopleApi(this.api.getInstance() as any);
    from(file.arrayBuffer())
      .pipe(
        map((buffer) => new Blob([buffer], { type: file.type }) as any),
        switchMap((blob) => from(api.updateAvatarImage('-me-', blob) as Promise<any>)),
        catchError((error) => {
          this.notification.showError(this.translate.instant('DMS.UI.B357F3'));
          return of(error ? null : null);
        })
      )
      .subscribe(() => {
        this.avatarBusy.set(false);
        // the rendition is made after the upload answers, so keep looking
        this.avatar.refresh(5);
      });
  }

  removeAvatar(): void {
    this.avatarBusy.set(true);
    const api = new PeopleApi(this.api.getInstance() as any);
    from(api.deleteAvatarImage('-me-') as Promise<any>)
      .pipe(catchError(() => of(null)))
      .subscribe(() => {
        this.avatarBusy.set(false);
        this.avatar.refresh(0, false);
      });
  }

  // ---- branding -------------------------------------------------------------

  /** Reads the picked logo into the document itself, as a data URI. */
  pickLogo(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      this.notification.showError(this.translate.instant('DMS.UI.A39AA9'));
      return;
    }
    if (file.size > BrandingService.MAX_LOGO_BYTES) {
      this.notification.showError(this.translate.instant('DMS.UI.618305'));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.brandLogo = String(reader.result ?? '');
    };
    reader.readAsDataURL(file);
  }

  /** Back to the logo the application ships with. */
  resetLogo(): void {
    this.brandLogo = '';
    this.brandLogoCleared = true;
  }

  saveBranding(): void {
    this.brandBusy.set(true);
    const logo = this.brandLogo || (this.brandLogoCleared ? '' : this.branding.customLogo() ? this.branding.logo() : '');

    // the stylesheet's own colour is stored as "no colour", so a deployment
    // that never chose one keeps following the stylesheet if it ever changes
    const primary = this.brandPrimary === BrandingService.DEFAULT_PRIMARY ? '' : this.brandPrimary;

    this.branding
      .save(this.brandName, logo, primary)
      .pipe(catchError(() => of(null)))
      .subscribe((result) => {
        this.brandBusy.set(false);
        if (result === null) {
          this.notification.showError(this.translate.instant('DMS.UI.204199'));
          return;
        }
        this.brandLogo = '';
        this.brandLogoCleared = false;
        this.brandName = this.branding.name();
        this.brandPrimary = this.branding.primary() || BrandingService.DEFAULT_PRIMARY;
        this.notification.showInfo(this.translate.instant('DMS.UI.E9F51B'));
      });
  }

  select(section: SettingsSection): void {
    // the workflow section owns a real URL so the catalogue's old route can
    // redirect into it — same pattern the migration center follows below
    if (section === 'workflow') {
      if (this.route.snapshot.data?.['section'] === 'workflow') {
        this.active = 'workflow';
        return;
      }
      this.router.navigate(['/settings/workflows']);
      return;
    }
    // the migration center owns real URLs so its tabs are deep-linkable;
    // every other section stays an in-place switch exactly as before
    if (section === 'migration') {
      // this instance was itself reached through a migration URL — the
      // in-place switch to another section left the router parked on
      // /settings/data-migration, and navigating to the URL you are already
      // on is ignored, which is what made the rail entry feel dead. Flip the
      // panel back instead; the center reads its tab from the route again.
      if (this.route.snapshot.data?.['section'] === 'migration') {
        this.active = 'migration';
        return;
      }
      this.router.navigate(['/settings/data-migration']);
      return;
    }
    this.active = section;
  }

  // ---- workflow ------------------------------------------------------------

  /**
   * The administrator's switch for the whole workflow feature: off hides
   * "งานของฉัน" and "การอนุมัติ" in the navigation and closes the routes behind
   * them, on brings them back. The builder stays reachable either way — the
   * switch has to live somewhere it can be turned back on.
   */
  saveWorkflowEnabled(enabled: boolean): void {
    this.workflowBusy.set(true);
    this.workflowSettings
      .setEnabled(enabled)
      .pipe(catchError(() => of(null)))
      .subscribe((result) => {
        this.workflowBusy.set(false);
        if (result === null) {
          this.notification.showError(this.translate.instant('DMS.UI.AAF711'));
          return;
        }
        this.notification.showInfo(this.translate.instant('DMS.UI.F47A3A'));
      });
  }

  /**
   * Categories this person may open — administration is for administrators,
   * and every section answers to the menu-visibility policies on top of that:
   * a policy can withhold "ฟิลด์ข้อมูล" from a group, but cannot hand
   * "เครื่องมือผู้ดูแล" to someone who is not an administrator.
   */
  visibleSections(): { id: SettingsSection; title: string; description: string; icon: string }[] {
    // administration, the workflow switch and the AI assistant (which holds
    // API keys) are for administrators
    return this.sections.filter(
      (section) =>
        (!['admin', 'workflow', 'assistant', 'migration'].includes(section.id) || this.person()?.isAdmin) &&
        this.menuPerms.isMenuAllowed(`app.settings.${section.id}`)
    );
  }

  /**
   * The section being shown must be one this person may see — policies are
   * read after the page opens, so the first choice is corrected once they
   * arrive.
   *
   * Both answers arrive on their own clock: the policies through `whenLoaded`
   * (which replays instantly once loaded) and the person through `loadPerson`.
   * Judging before the person is known would treat an administrator as a
   * stranger — on the migration route that resets `active` to `profile` just
   * after `ngOnInit` set it, which is why this waits and `loadPerson` runs the
   * check again once the person is in.
   */
  private ensureActiveAllowed(): void {
    if (this.person() === null) {
      return;
    }
    const visible = this.visibleSections();
    if (!visible.some((s) => s.id === this.active)) {
      this.active = visible[0]?.id ?? 'profile';
    }
  }

  // ---- what the repository supplies ---------------------------------------

  private loadPerson(): void {
    const instance: any = this.api.getInstance();
    from(instance.contentClient.callApi('/people/-me-', 'GET', {}, {}, {}, {}, null, ['application/json'], ['application/json']) as Promise<any>)
      .pipe(
        map((response: any) => response?.entry),
        catchError(() => of(null))
      )
      .subscribe((entry: any) => {
        if (entry) {
          this.person.set({
            id: entry.id,
            displayName: entry.displayName || [entry.firstName, entry.lastName].filter(Boolean).join(' ') || entry.id,
            email: entry.email || '—',
            jobTitle: entry.jobTitle || '—',
            enabled: !!entry.enabled,
            isAdmin: !!entry.capabilities?.isAdmin
          });

          // policies may already be in — re-check the open section now that
          // the person it applies to is known
          this.ensureActiveAllowed();

          // the category is offered only to the people its screens are for
          if (
            !entry.capabilities?.isAdmin &&
            (this.active === 'admin' || this.active === 'assistant' || this.active === 'workflow' || this.active === 'migration')
          ) {
            this.active = 'profile';
          }
        }
      });
  }

  private loadRepository(): void {
    const instance: any = this.api.getInstance();
    from(instance.discoveryClient.callApi('/discovery', 'GET', {}, {}, {}, {}, null, ['application/json'], ['application/json']) as Promise<any>)
      .pipe(
        map((response: any) => response?.entry?.repository),
        catchError(() => of(null))
      )
      .subscribe((repository: any) => {
        if (repository) {
          this.repository.set({
            edition: repository.edition ?? '—',
            version: repository.version?.display ?? '—',
            modules: (repository.modules ?? []).length,
            readOnly: !!repository.status?.isReadOnly,
            auditEnabled: !!repository.status?.isAuditEnabled
          });
        }
      });
  }

  // ---- preferences, kept on the server so they follow the user ------------

  private loadPreferences(): void {
    this.displayPrefs.load().subscribe(({ pageSize, landing }) => {
      if (this.pageSizes.includes(pageSize)) {
        this.pageSize = pageSize;
      }
      this.landing = landing;
    });
  }

  savePageSize(value: number | string): void {
    this.pageSize = Number(value);
    this.store(this.displayPrefs.savePageSize(this.pageSize));
  }

  saveLanding(value: string): void {
    this.landing = value;
    this.store(this.displayPrefs.saveLanding(value));
  }

  private store(write: Observable<void>): void {
    write.subscribe({
      next: () => this.notification.showInfo(this.translate.instant('DMS.UI.F47A3A')),
      error: () => this.notification.showError(this.translate.instant('DMS.UI.AAF711'))
    });
  }

  toggleTheme(): void {
    this.theme.toggle();
  }

  // ---- branding, read from the application configuration ------------------

  get appName(): string {
    return this.config.get<string>('application.name', 'Alfresco');
  }

  get appLogo(): string {
    return this.config.get<string>('application.logo', 'assets/images/alfresco-logo.svg');
  }

  get appVersion(): string {
    return this.config.get<string>('application.version', '');
  }

  initial(name?: string): string {
    return (name || '?').trim().charAt(0).toUpperCase();
  }
  /**
   * Switches the interface language, and remembers it the way ADF does.
   *
   * ADF stores preferences under the signed-in user, so setting the locale here
   * leaves the sign-in screen — which reads the same preference under `GUEST`,
   * before anyone is signed in — on the previous language. The guest key is
   * written too so one choice covers the whole application.
   */
  changeLanguage(key: string): void {
    this.language = key;
    this.languageService.use(key);
  }
}
